import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

export default function ApplicationForm() {
    const { t } = useTranslation();

    // State for form data
    const [formData, setFormData] = useState({
        // Details about buyer
        companyName: '',
        contactPerson: '',
        address: '',
        email: '',
        telephone: '',

        // Products and services
        frfKit: {
            selected: false,
            options: [] // HKFRS, HKFRS-PE, SME-FRS, All
        },
        customizableFS: {
            selected: false,
            options: [] // HKFRS, HKFRS-PE, SME-FRS, All
        },
        version: '', // Standalone, Cloud
        noOfDevices: '',
        noOfTrainingAttendees: '',
        trainingLocation: '', // JKL, Others
        trainingLocationOther: '',
        hardware: false,
        consultancyServices: [],

        // Signature section
        signeeName: '',
        signeeJobTitle: '',
        signatureDate: ''
    });

    const [submitted, setSubmitted] = useState(false);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleCheckboxChange = (category, value) => {
        if (category === 'consultancyServices') {
            setFormData(prev => ({
                ...prev,
                consultancyServices: prev.consultancyServices.includes(value)
                    ? prev.consultancyServices.filter(item => item !== value)
                    : [...prev.consultancyServices, value]
            }));
        } else if (category === 'hardware') {
            setFormData(prev => ({
                ...prev,
                hardware: !prev.hardware
            }));
        }
    };

    const handleProductOptionChange = (product, option) => {
        setFormData(prev => ({
            ...prev,
            [product]: {
                selected: true,
                options: prev[product].options.includes(option)
                    ? prev[product].options.filter(item => item !== option)
                    : [...prev[product].options, option]
            }
        }));
    };

    const formatFormDataForWhatsApp = () => {
        // Format all form data into a readable WhatsApp message
        let message = "🔵 *AQUA AUDIT SYSTEM - APPLICATION FORM*\n\n";
        
        message += "📋 *BUYER DETAILS*\n";
        message += `Company Name: ${formData.companyName}\n`;
        message += `Contact Person: ${formData.contactPerson}\n`;
        message += `Telephone: ${formData.telephone}\n`;
        message += `Email: ${formData.email}\n`;
        message += `Address: ${formData.address}\n\n`;
        
        message += "🛍️ *PRODUCTS & SERVICES*\n\n";
        
        // FRF Kit
        if (formData.frfKit.options.length > 0) {
            message += `FRF (Built-in SQM Kit): ${formData.frfKit.options.join(', ')}\n`;
        }
        
        // Customizable FS
        if (formData.customizableFS.options.length > 0) {
            message += `Customisable Financial Statements: ${formData.customizableFS.options.join(', ')}\n`;
        }
        
        // Version
        if (formData.version) {
            message += `Version: ${formData.version}\n`;
        }
        
        // Devices and Training
        if (formData.noOfDevices) {
            message += `No. of Devices: ${formData.noOfDevices}\n`;
        }
        
        if (formData.noOfTrainingAttendees) {
            message += `Training Attendees: ${formData.noOfTrainingAttendees}\n`;
        }
        
        // Training Location
        if (formData.trainingLocation) {
            message += `Training Location: ${formData.trainingLocation}`;
            if (formData.trainingLocation === 'Others' && formData.trainingLocationOther) {
                message += ` - ${formData.trainingLocationOther}`;
            }
            message += '\n';
        }
        
        // Hardware
        if (formData.hardware) {
            message += `Hardware: AQUA all-in-one-box ✓\n`;
        }
        
        // Consultancy Services
        if (formData.consultancyServices.length > 0) {
            message += `\nConsultancy Services:\n`;
            formData.consultancyServices.forEach(service => {
                message += `  • ${service}\n`;
            });
        }
        
        message += "\n✍️ *SIGNATURE*\n";
        message += `Name: ${formData.signeeName}\n`;
        message += `Job Title: ${formData.signeeJobTitle}\n`;
        message += `Date: ${formData.signatureDate}\n\n`;
        
        message += "---\n";
        message += `Submitted: ${new Date().toLocaleString()}\n`;
        message += "Via AQUA Website Application Form";
        
        return message;
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        
        // Log action as per user rules
        console.log(`[LOG] Application form submitted at ${new Date().toISOString()}`);
        console.log('[LOG] Form data:', formData);
        
        // Format the data for WhatsApp
        const message = formatFormDataForWhatsApp();
        
        // WhatsApp Business number (update this with actual number)
        // Format: country code + number without + or spaces
        // Example: 85237036123 for Hong Kong number (852) 3703 6123
        const whatsappNumber = '85268793384'; // Update with actual WhatsApp Business number
        
        // URL encode the message
        const encodedMessage = encodeURIComponent(message);
        
        // Create WhatsApp URL
        const whatsappURL = `https://wa.me/${whatsappNumber}?text=${encodedMessage}`;
        
        // Log the WhatsApp action
        console.log(`[LOG] Opening WhatsApp to send form data at ${new Date().toISOString()}`);
        
        // Open WhatsApp in new window
        window.open(whatsappURL, '_blank');
        
        // Mark as submitted
        setSubmitted(true);
    };

    if (submitted) {
        return (
            <div className="container tw-my-20 tw-text-center">
                <div className="tw-bg-green-50 tw-border tw-border-green-200 tw-rounded-lg tw-p-8 tw-max-w-2xl tw-mx-auto">
                    <h2 className="tw-text-3xl tw-font-bold tw-text-green-800 tw-mb-4">Thank You!</h2>
                    <p className="tw-text-lg tw-text-gray-700 tw-mb-2">
                        Your application has been submitted successfully.
                    </p>
                    <p className="tw-text-gray-600">
                        Our Account Executive will contact you as soon as possible.
                    </p>
                    <div className="tw-flex tw-gap-4 tw-justify-center tw-mt-6">
                        <button 
                            onClick={() => setSubmitted(false)}
                            className="tw-bg-blue-900 tw-text-white tw-px-6 tw-py-2 tw-rounded hover:tw-bg-blue-800"
                        >
                            Submit Another Form
                        </button>
                        <Link 
                            to="/"
                            className="tw-bg-gray-600 tw-text-white tw-px-6 tw-py-2 tw-rounded hover:tw-bg-gray-700 tw-inline-flex tw-items-center"
                        >
                            Back to Home
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="container tw-my-10">
            {/* Back Button */}
            <div className="tw-mb-6">
                <Link to="/" className="tw-inline-flex tw-items-center tw-text-blue-900 hover:tw-text-blue-700 tw-font-semibold">
                    <svg className="tw-w-5 tw-h-5 tw-mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                    </svg>
                    Back to Home
                </Link>
            </div>
            
            {/* Header */}
            <div className="tw-text-center tw-mb-8">
                <h1 className="tw-text-4xl tw-font-bold tw-text-blue-900 tw-mb-2">
                    JKL LIMITED
                </h1>
                <h2 className="tw-text-3xl tw-font-semibold tw-text-gray-700">
                    AQUA Audit System
                </h2>
                <h3 className="tw-text-xl tw-text-gray-600 tw-mt-4">
                    Application Form
                </h3>
            </div>

            <form onSubmit={handleSubmit} className="tw-max-w-5xl tw-mx-auto">
                {/* Details about buyer */}
                <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-6 tw-mb-6">
                    <h3 className="tw-text-2xl tw-font-bold tw-text-blue-900 tw-mb-6 tw-border-b tw-pb-3">
                        Details About Buyer
                    </h3>
                    
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4">
                        <div className="md:tw-col-span-2">
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                Company Name <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="companyName"
                                value={formData.companyName}
                                onChange={handleInputChange}
                                required
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>

                       

                        <div className="md:tw-col-span-2">
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                Address <span className="tw-text-red-500">*</span>
                            </label>
                            <textarea
                                name="address"
                                value={formData.address}
                                onChange={handleInputChange}
                                required
                                rows="3"
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>

                        <div className="md:tw-col-span-2">
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                E-mail Address <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="email"
                                name="email"
                                value={formData.email}
                                onChange={handleInputChange}
                                required
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>
                    </div>
                </div>

                {/* Products and Services */}
                <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-6 tw-mb-6">
                    <h3 className="tw-text-2xl tw-font-bold tw-text-blue-900 tw-mb-6 tw-border-b tw-pb-3">
                        Products and Services
                    </h3>

          
                    {/* Version */}
                    <div className="tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded">
                        <label className="tw-block tw-font-semibold tw-mb-3">
                            Version
                        </label>
                        <div className="tw-flex tw-gap-6">
                            <label className="tw-flex tw-items-center tw-cursor-pointer">
                                <input
                                    type="radio"
                                    name="version"
                                    value="Standalone"
                                    checked={formData.version === 'Standalone'}
                                    onChange={handleInputChange}
                                    className="tw-mr-2 tw-w-4 tw-h-4"
                                />
                                <span>Standalone (On-premises)</span>
                            </label>
                            <label className="tw-flex tw-items-center tw-cursor-pointer">
                                <input
                                    type="radio"
                                    name="version"
                                    value="Cloud"
                                    checked={formData.version === 'Cloud'}
                                    onChange={handleInputChange}
                                    className="tw-mr-2 tw-w-4 tw-h-4"
                                />
                                <span>Cloud version</span>
                            </label>
                        </div>
                    </div>

                    {/* Number of devices and training */}
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-2 tw-gap-4 tw-mb-6">
                        <div>
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                No. of Devices (PC and notebook) to be installed
                            </label>
                            <input
                                type="number"
                                name="noOfDevices"
                                value={formData.noOfDevices}
                                onChange={handleInputChange}
                                min="0"
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                No. of People Attending the Induction Training
                            </label>
                            <input
                                type="number"
                                name="noOfTrainingAttendees"
                                value={formData.noOfTrainingAttendees}
                                onChange={handleInputChange}
                                min="0"
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>
                    </div>

                    {/* Training location */}
                    <div className="tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded">
                        <label className="tw-block tw-font-semibold tw-mb-3">
                            Training Location
                        </label>
                        <div className="tw-flex tw-flex-col tw-gap-3">
                            <label className="tw-flex tw-items-center tw-cursor-pointer">
                                <input
                                    type="radio"
                                    name="trainingLocation"
                                    value="JKL"
                                    checked={formData.trainingLocation === 'JKL'}
                                    onChange={handleInputChange}
                                    className="tw-mr-2 tw-w-4 tw-h-4"
                                />
                                <span>JKL training centre</span>
                            </label>
                            <div className="tw-flex tw-items-center">
                                <label className="tw-flex tw-items-center tw-cursor-pointer">
                                    <input
                                        type="radio"
                                        name="trainingLocation"
                                        value="Others"
                                        checked={formData.trainingLocation === 'Others'}
                                        onChange={handleInputChange}
                                        className="tw-mr-2 tw-w-4 tw-h-4"
                                    />
                                    <span className="tw-mr-2">Others (please specify):</span>
                                </label>
                                <input
                                    type="text"
                                    name="trainingLocationOther"
                                    value={formData.trainingLocationOther}
                                    onChange={handleInputChange}
                                    disabled={formData.trainingLocation !== 'Others'}
                                    className="tw-flex-1 tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500 disabled:tw-bg-gray-200"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Hardware */}
                    <div className="tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded">
                        <label className="tw-flex tw-items-center tw-cursor-pointer">
                            <input
                                type="checkbox"
                                checked={formData.hardware}
                                onChange={() => handleCheckboxChange('hardware')}
                                className="tw-mr-2 tw-w-4 tw-h-4"
                            />
                            <span className="tw-font-semibold">Hardware - AQUA all-in-one-box (Optional)</span>
                        </label>
                    </div>

                    {/* Consultancy services */}
                    <div className="tw-mb-6 tw-p-4 tw-bg-gray-50 tw-rounded">
                        <label className="tw-block tw-font-semibold tw-mb-3">
                            Consultancy Services
                        </label>
                        <div className="tw-space-y-2">
                            {[
                                'Tailor-made full set of financial statements',
                                'Alternation of audit manual and worksheet templates',
                                'Other services'
                            ].map(service => (
                                <label key={service} className="tw-flex tw-items-center tw-cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={formData.consultancyServices.includes(service)}
                                        onChange={() => handleCheckboxChange('consultancyServices', service)}
                                        className="tw-mr-2 tw-w-4 tw-h-4"
                                    />
                                    <span>{service}</span>
                                </label>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Instructions */}
                <div className="tw-bg-blue-50 tw-border tw-border-blue-200 tw-rounded-lg tw-p-6 tw-mb-6">
                    <h3 className="tw-text-xl tw-font-bold tw-text-blue-900 tw-mb-4">
                        Instructions
                    </h3>
                    <ol className="tw-list-decimal tw-list-inside tw-space-y-2 tw-text-gray-700">
                        <li>Please read carefully the contents in these briefing materials.</li>
                        <li>All prices are effective from 1 January 2025 (based on the date received this request).</li>
                        <li>Please complete the customer information table and products and services table.</li>
                        <li>Please e-mail/whatsapp the duly completed requisition to sales@JKL-Aqua.com or (852) 6879 3384 (WhatsApp).</li>
                        <li>Our Account Executive will contact you as soon as receiving your enquiry.</li>
                        <li>For any enquiry, please send an enquiry to sales@JKL-Aqua.com or dial (852) 3703 6123  or (852) 6879 3384 (WhatsApp) during office hours.</li>
                    </ol>
                </div>

                {/* Terms and Conditions */}
                <div className="tw-bg-yellow-50 tw-border tw-border-yellow-200 tw-rounded-lg tw-p-6 tw-mb-6">
                    <h3 className="tw-text-xl tw-font-bold tw-text-yellow-900 tw-mb-4">
                        Terms and Conditions
                    </h3>
                    <ol className="tw-list-decimal tw-list-inside tw-space-y-2 tw-text-gray-700">
                        <li>Please provide complete and accurate contact information. Incomplete and inaccurate information may preclude follow-up procedure.</li>
                        <li>Buyer information will be subject to the protection of Personal Data Privacy Ordinance. All information will be destroyed within 30 days after completion of order.</li>
                    </ol>
                </div>

                {/* Signature Section */}
                <div className="tw-bg-white tw-shadow-lg tw-rounded-lg tw-p-6 tw-mb-6">
                    <h3 className="tw-text-2xl tw-font-bold tw-text-blue-900 tw-mb-6 tw-border-b tw-pb-3">
                        Signature
                    </h3>
                    
                    <div className="tw-grid tw-grid-cols-1 md:tw-grid-cols-3 tw-gap-4">
                        <div>
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                Name <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="signeeName"
                                value={formData.signeeName}
                                onChange={handleInputChange}
                                required
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                Job Title <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="text"
                                name="signeeJobTitle"
                                value={formData.signeeJobTitle}
                                onChange={handleInputChange}
                                required
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>

                        <div>
                            <label className="tw-block tw-font-semibold tw-mb-2">
                                Date <span className="tw-text-red-500">*</span>
                            </label>
                            <input
                                type="date"
                                name="signatureDate"
                                value={formData.signatureDate}
                                onChange={handleInputChange}
                                required
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded tw-px-4 tw-py-2 focus:tw-outline-none focus:tw-border-blue-500"
                            />
                        </div>
                    </div>

                    <div className="tw-mt-4 tw-p-4 tw-bg-gray-50 tw-rounded tw-text-sm tw-text-gray-600">
                        <p><strong>Note:</strong> By submitting this form, you acknowledge that you have read and agreed to the terms and conditions stated above. In a physical submission, this section would include fields for Signature and Company Chop.</p>
                    </div>
                </div>

                {/* Submit Button */}
                <div className="tw-text-center tw-mb-8">
                    <button
                        type="submit"
                        className="tw-bg-blue-900 tw-text-white tw-font-bold tw-px-12 tw-py-4 tw-rounded-lg tw-text-lg hover:tw-bg-blue-800 tw-transition-colors tw-shadow-lg"
                    >
                        Submit Application
                    </button>
                </div>

                {/* Contact Information Footer */}
                <div className="tw-text-center tw-text-gray-600 tw-text-sm">
                    <p>For assistance, contact us at:</p>
                    <p className="tw-font-semibold">sales@JKL-Aqua.com | (852) 3703 6123</p>
                </div>
            </form>
        </div>
    );
}

